<template>
  <div
    v-if="page"
    class="ns"
  >
    <!-- Hero -->
    <section class="ns-hero">
      <div class="ns-container">
        <div class="ns-hero-grid">
          <h1 class="ns-hero-title">
            <span
              v-if="titleParts.prefixHtml"
              class="ns-line"
              v-html="titleParts.prefixHtml"
            />
            <span class="ns-line"><em>{{ titleParts.accent }}</em></span>
          </h1>
          <div class="ns-hero-body">
            <p class="ns-hero-lede">
              {{ page.intro_copy }}
            </p>
            <div class="ns-hero-cta">
              <router-link
                :to="filteredTracksRouteTo || { name: 'track-search' }"
                class="button is-secondary is-large is-rounded is-accent"
              >
                Listen to the Collection
              </router-link>
            </div>
          </div>
          <div
            class="ns-anim-frame"
            aria-hidden="true"
          >
            <svg
              viewBox="0 0 300 100"
              preserveAspectRatio="none"
            >
              <defs>
                <clipPath id="ns-playhead-clip">
                  <rect
                    class="ns-clip-rect"
                    x="0"
                    y="0"
                    width="300"
                    height="100"
                  />
                </clipPath>
              </defs>
              <g ref="wfBase" />
              <g
                ref="wfPlayed"
                clip-path="url(#ns-playhead-clip)"
              />
              <rect
                class="ns-cursor"
                x="0"
                y="16"
                width="1"
                height="68"
              />
            </svg>
          </div>
        </div>
      </div>
    </section>

    <!-- Answer snippet as pull quote -->
    <section
      v-if="page.answer_snippet"
      class="ns-section"
    >
      <div class="ns-container">
        <div class="ns-pullquote">
          <p>{{ page.answer_snippet }}</p>
        </div>
      </div>
    </section>

    <!-- Tracks -->
    <section
      v-if="page.tracks && page.tracks.length"
      class="ns-section ns-section--light"
    >
      <div class="ns-container">
        <div class="ns-section-head">
          <span class="ns-section-index">Chapter I.</span>
          <div>
            <h2 class="ns-section-title">
              Tracks From The Collection.
            </h2>
            <p class="ns-section-sub">
              {{ trackCountLabel }}. The full collection is a click away.
            </p>
            <p
              v-for="(para, i) in bodyCopyParagraphs"
              :key="i"
              class="ns-section-sub"
            >
              {{ para }}
            </p>
          </div>
        </div>
        <div class="ns-tracks-list">
          <track-list-item
            v-for="track in page.tracks"
            :key="track.pk"
            :track="track"
          />
        </div>
        <div class="ns-tracks-cta">
          <router-link
            :to="filteredTracksRouteTo || { name: 'track-search' }"
            class="button is-secondary is-large is-rounded is-accent"
          >
            Listen to the full collection
          </router-link>
        </div>
      </div>
    </section>

    <!-- Best for -->
    <section
      v-if="page.best_for && page.best_for.length"
      class="ns-section"
    >
      <div class="ns-container">
        <div class="ns-section-head">
          <span class="ns-section-index">Chapter II.</span>
          <h2 class="ns-section-title">
            Where It Belongs.
          </h2>
        </div>
        <div class="ns-best">
          <div
            v-for="(item, i) in page.best_for"
            :key="i"
            class="ns-best-item"
          >
            <span class="ns-best-idx">{{ romanNumeral(i + 1) }}.</span>
            <span class="ns-best-label">{{ item }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Licensing -->
    <section class="ns-section">
      <div class="ns-container">
        <div class="ns-section-head">
          <span class="ns-section-index">Chapter III.</span>
          <h2 class="ns-section-title">
            One Licence. No Small Print.
          </h2>
        </div>
        <div class="ns-licensing">
          <div>
            <h3 class="ns-licensing-headline">
              Cleared For Commercial Use.
              <em>Every Track.</em>
            </h3>
            <div class="ns-marks">
              <span>Royalty-free</span>
              <span>No Content ID</span>
              <span>Perpetual</span>
              <span>Worldwide</span>
            </div>
          </div>
          <div class="ns-licensing-body">
            <p>
              One simple licence covers YouTube, social, ads, film and client work &mdash;
              no recurring royalties and no Content ID or copyright claims.
            </p>
            <p>
              Publish something today and it stays cleared forever, whether you renew
              or not.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section
      v-if="page.faq && page.faq.length"
      class="ns-section"
    >
      <div class="ns-container">
        <div class="ns-section-head">
          <span class="ns-section-index">Chapter IV.</span>
          <h2 class="ns-section-title">
            Things People Ask, Before They License.
          </h2>
        </div>
        <div class="ns-faq-list">
          <div
            v-for="(qa, i) in page.faq"
            :key="i"
            class="ns-faq-item"
          >
            <div class="ns-faq-q">
              {{ qa.q }}
            </div>
            <div class="ns-faq-a">
              {{ qa.a }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Explore related -->
    <section
      v-if="hasRelated"
      class="ns-section"
    >
      <div class="ns-container">
        <div class="ns-section-head">
          <span class="ns-section-index">Chapter V.</span>
          <h2 class="ns-section-title">
            Explore More Music.
          </h2>
        </div>
        <div
          v-if="related.hubs.length"
          class="ns-related-block"
        >
          <h3 class="ns-related-heading">
            The Broader Collection
          </h3>
          <ul class="ns-related-list">
            <li
              v-for="link in related.hubs"
              :key="link.slug"
            >
              <router-link :to="lpRoute(link)">
                {{ link.title }}
              </router-link>
            </li>
          </ul>
        </div>
        <div
          v-if="related.siblings.length"
          class="ns-related-block"
        >
          <h3 class="ns-related-heading">
            Similar Combinations
          </h3>
          <ul class="ns-related-list">
            <li
              v-for="link in related.siblings"
              :key="link.slug"
            >
              <router-link :to="lpRoute(link)">
                {{ link.title }}
              </router-link>
            </li>
          </ul>
        </div>
        <div
          v-if="related.spokes.length"
          class="ns-related-block"
        >
          <h3 class="ns-related-heading">
            Curated Collections
          </h3>
          <ul class="ns-related-list">
            <li
              v-for="link in related.spokes"
              :key="link.slug"
            >
              <router-link :to="lpRoute(link)">
                {{ link.title }}
              </router-link>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- Finale -->
    <section class="ns-finale">
      <div class="ns-container">
        <h2>
          Listen To The Collection
          <router-link
            :to="filteredTracksRouteTo || { name: 'track-search' }"
            class="ns-finale-title-link"
          >
            <em>{{ page.title }}.</em>
          </router-link>
        </h2>
        <div class="ns-finale-cta">
          <router-link
            :to="filteredTracksRouteTo || { name: 'track-search' }"
            class="button is-secondary is-large is-rounded is-accent"
          >
            Listen to the Collection
          </router-link>
          <a
            class="button is-secondary is-large is-rounded ghost-inverse"
            @click="$store.dispatch('common/startSignupWorkflow')"
          >
            {{ $store.getters['subscriptions/signupButtonLabel'] }}
          </a>
        </div>
      </div>
    </section>

    <the-footer />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import keywordLandingPagesClient from '@/api/keywordLandingPages.js'
import TheFooter from '@/components/TheFooter.vue'
import TrackListItem from '@/components/TrackListItem.vue'

const WAFFLE_SWITCH = 'keyword_landing_pages'

const PAGE_DATA_SCRIPT_ID = '__lp_page_data__'

// KLP filter dims are singular; TrackSearch options are plural.
const FILTER_DIMENSION_TO_TRACK_SEARCH_FIELD = {
  genre: 'genres',
  mood: 'moods',
  theme: 'themes',
  instrument: 'instruments'
}

const ROMAN = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x']

// Small 1..10 lookup for spelling out track counts in prose. Numbers past
// ten fall back to numerals — the assign command's default limit is 5 so we
// almost never hit that.
const NUMBER_WORDS = [
  'Zero', 'One', 'Two', 'Three', 'Four', 'Five',
  'Six', 'Seven', 'Eight', 'Nine', 'Ten'
]

// The baked data belongs to the page the document was prerendered for. After navigating
// to another landing page in the SPA it's still in the document, so only use it if it's
// for the requested slug; otherwise the previous page's content would show until (or,
// if the fetch fails, instead of) the right one.
function readBakedPageData (slug) {
  if (typeof window === 'undefined') return null
  let data = window.__LP_PAGE__ || null
  if (!data) {
    const el = document.getElementById(PAGE_DATA_SCRIPT_ID)
    if (!el) return null
    try {
      data = JSON.parse(el.textContent)
    } catch (e) {
      return null
    }
  }
  return data && data.slug === slug ? data : null
}

function isPrerenderContext () {
  return Boolean(
    typeof window !== 'undefined' &&
    window.__PRERENDER_INJECTED &&
    window.__PRERENDER_INJECTED.isPrerender
  )
}

export default {
  name: 'KeywordLandingPage',

  components: {
    TheFooter,
    TrackListItem
  },

  props: {
    slug: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      page: readBakedPageData(this.slug)
    }
  },

  computed: {
    ...mapGetters({ isFeatureEnabled: 'waffle/isFeatureEnabled' }),

    titleParts () {
      // Last word of the title is accented; everything before it is the
      // prefix line. Any "for" (case-insensitive) inside the prefix gets the
      // italic superscript treatment so titles like "Music for Weddings"
      // read as MUSIC / for / WEDDINGS. Titles with no "for" simply split
      // into prefix + accent, so "Corporate Background Music" reads as
      // CORPORATE BACKGROUND / MUSIC.
      const title = (this.page && this.page.title) || ''
      const words = title.split(/\s+/).filter(Boolean)
      if (words.length === 0) return { prefixHtml: '', accent: '' }
      if (words.length === 1) return { prefixHtml: '', accent: words[0] }
      const escape = s => s.replace(/[&<>"']/g, c => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
      ))
      const prefix = words.slice(0, -1).map(w =>
        w.toLowerCase() === 'for'
          ? '<span class="ns-line-for">for</span>'
          : escape(w)
      ).join(' ')
      return { prefixHtml: prefix, accent: words[words.length - 1] }
    },

    trackCountLabel () {
      const n = (this.page && this.page.tracks) ? this.page.tracks.length : 0
      const word = NUMBER_WORDS[n] || String(n)
      const noun = n === 1 ? 'track' : 'tracks'
      return `${word} hand-picked ${noun}`
    },

    filteredTracksRouteTo () {
      // Returns null on hub pages with no filters set — the CTA is hidden.
      const query = {}
      const filters = (this.page && this.page.filters) || {}
      for (const [dimension, slug] of Object.entries(filters)) {
        if (!slug) continue
        const field = FILTER_DIMENSION_TO_TRACK_SEARCH_FIELD[dimension]
        if (!field) continue
        query[field] = slug
      }
      if (Object.keys(query).length === 0) return null
      return { name: 'track-search', query }
    },

    related () {
      return (this.page && this.page.related) || { hubs: [], siblings: [], spokes: [] }
    },

    bodyCopyParagraphs () {
      const raw = (this.page && this.page.body_copy) || ''
      return raw.split(/\n+/).map(p => p.trim()).filter(p => p.length > 0)
    },

    hasRelated () {
      const r = this.related
      return r.hubs.length > 0 || r.siblings.length > 0 || r.spokes.length > 0
    }
  },

  async mounted () {
    if (isPrerenderContext()) {
      // Bake the page data into the snapshot so the SPA can hydrate the
      // first paint without an API round-trip or a "Loading..." flash.
      if (this.page && !document.getElementById(PAGE_DATA_SCRIPT_ID)) {
        const script = document.createElement('script')
        script.type = 'application/json'
        script.id = PAGE_DATA_SCRIPT_ID
        script.textContent = JSON.stringify(this.page)
        document.head.appendChild(script)
      }
      await this.$nextTick()
      this.buildWaveform()
    } else {
      // Belt-and-braces gate: if the keyword_landing_pages waffle switch is
      // off, bail to not-found before hitting the API. The retrieve endpoint
      // also returns 404 in that case (see catch block below) but this saves
      // a network round-trip and avoids briefly showing baked content from a
      // previous build when the switch has since been flipped off.
      await this.$store.dispatch('waffle/fetch')
      if (!this.isFeatureEnabled(WAFFLE_SWITCH)) {
        this.$router.replace({ name: 'not-found' })
        return
      }
      try {
        const { data } = await keywordLandingPagesClient.retrieve(this.slug)
        this.page = data
        if (this.page.tracks && this.page.tracks.length > 0) {
          this.$store.dispatch('player/loadTracks', this.page.tracks)
        }
        await this.$nextTick()
        this.buildWaveform()
      } catch (err) {
        // 404 means the slug isn't a published landing page — show the SPA's
        // not-found page rather than a broken empty layout.
        if (err.response && err.response.status === 404) {
          this.$router.replace({ name: 'not-found' })
          return
        }
      }
    }
    await this.$nextTick()
    if (this.$meta) this.$meta().refresh()
    await this.$nextTick()
    document.dispatchEvent(new Event('render-event'))
  },

  methods: {
    romanNumeral (n) {
      return ROMAN[n - 1] || String(n)
    },

    buildWaveform () {
      const wfBase = this.$refs.wfBase
      const wfPlayed = this.$refs.wfPlayed
      if (!wfBase || !wfPlayed) return
      const svgns = 'http://www.w3.org/2000/svg'
      const bars = 80
      const w = 300 / bars
      const mkRect = (i, cls) => {
        const h = 8 + Math.abs(Math.sin(i * 0.42) * 26 + Math.cos(i * 0.19) * 18)
        const y = (100 - h) / 2
        const r = document.createElementNS(svgns, 'rect')
        r.setAttribute('x', (i * w).toFixed(2))
        r.setAttribute('y', y.toFixed(2))
        r.setAttribute('width', (w - 1).toFixed(2))
        r.setAttribute('height', h.toFixed(2))
        r.setAttribute('rx', '1')
        r.setAttribute('class', cls)
        return r
      }
      for (let i = 0; i < bars; i++) {
        wfBase.appendChild(mkRect(i, 'ns-wf'))
        wfPlayed.appendChild(mkRect(i, 'ns-wf-played'))
      }
    },

    lpRoute (link) {
      return { name: 'keyword-landing-page', params: { slug: link.slug } }
    }
  },

  metaInfo () {
    if (!this.page) {
      return { title: 'Music Licensing' }
    }
    let url = ''
    if (typeof window !== 'undefined') {
      url = window.location.origin
      const path = this.$route.path
      if (path !== '/') {
        url += path
      }
    }
    // App.vue's titleTemplate ('%s | Sonata') adds the brand suffix, so we
    // pass the bare title — using title_tag here would double-suffix.
    const title = this.page.title
    const description = this.page.meta_description || this.page.intro_copy || ''
    return {
      title,
      meta: [
        { name: 'description', content: description },
        { property: 'og:url', content: url },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:image', content: '/img/sonata-meta.png' },
        { name: 'twitter:card', content: 'website' }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../assets/scss/variables";

$ns-bg-0:      $bluegrey0;
$ns-bg-1:      $bluegrey1;
$ns-ink-soft:  $bluegrey3;
$ns-ink-mid:   $bluegrey4;
$ns-ink-1:     $bluegrey5;
$ns-paper:     $bluegrey7;
$ns-rule:      rgba($bluegrey5, 0.10);
$ns-rule-hard: rgba($bluegrey5, 0.18);
$ns-accent:    $red;
$ns-accent-pale: $red-pale;
$ns-yellow:    $yellow;

.ns {
  background: $ns-bg-0;
  color: $ns-ink-1;
  font-family: "Euclid Circular", Avenir, sans-serif;
  font-size: 16px;
  // #app reserves 120px of top padding for the fixed .navbar and 160px of
  // bottom padding for its own default footer band. Eat both so the dark
  // ground reaches the top of the viewport and the site footer sits
  // directly at the page bottom — same negative-margin pattern the Home
  // page uses via #home.
  margin-top: -120px;
  padding-top: 120px;
  margin-bottom: -160px;
}

.ns-container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 32px;
}

// Sections ------------------------------------------------------------------

.ns-hero { padding: 84px 0 0; }
.ns-section {
  padding: 100px 0;
  border-top: 1px solid $ns-rule;
}

// Full-width light band — used for the tracks section so TrackListItem
// (which is designed for a white background) is legible. Section header
// text tokens are re-mapped to the standard bluegrey ramp on white.
.ns-section--light {
  background: $white;
  color: $bluegrey1;
  border-top-color: $bluegrey6;

  .ns-section-title { color: $bluegrey1; }
  .ns-section-sub   { color: $bluegrey3; }
}

.ns-section-head {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 40px;
  align-items: baseline;
  margin-bottom: 56px;
}
.ns-section-index {
  font-family: "Euclid Triangle", Avenir, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  font-weight: 500;
  font-style: italic;
  font-size: 11px;
  color: $ns-accent;
}
.ns-section-title {
  font-family: "GTAmerica", sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-size: 44px;
  line-height: 1;
  color: $ns-paper;
  text-wrap: balance;
  margin: 0;
}
.ns-section-sub {
  margin-top: 18px;
  max-width: 72ch;
  font-size: 15px;
  color: $ns-ink-mid;
  line-height: 1.6;
}

// Hero ---------------------------------------------------------------------

.ns-hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr);
  grid-template-areas:
    "title ."
    "body  anim";
  gap: 48px 72px;
  align-items: start;
}
.ns-hero-title {
  grid-area: title;
  font-family: "GTAmerica", sans-serif;
  font-weight: 600;
  color: $ns-paper;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-size: clamp(52px, 6.4vw, 84px);
  line-height: 0.94;
  text-wrap: balance;
  margin: 0;
}
.ns-hero-title ::v-deep .ns-line { display: block; }
.ns-hero-title ::v-deep .ns-line em {
  font-style: normal;
  color: $ns-accent;
}
// ::v-deep because the prefix line is injected via v-html — scoped rules
// don't apply to elements without the component's data-v attribute.
.ns-hero-title ::v-deep .ns-line-for {
  font-family: "Euclid Circular", sans-serif;
  font-weight: 400;
  font-style: italic;
  color: $ns-ink-mid;
  text-transform: lowercase;
  letter-spacing: 0;
  font-size: 0.42em;
  padding-left: 0.15em;
  vertical-align: 0.35em;
}
.ns-hero-body { grid-area: body; }
.ns-hero-lede {
  max-width: 44ch;
  font-size: 20px;
  line-height: 1.5;
  color: $ns-ink-1;
  font-weight: 400;
  margin: 0;
}
.ns-hero-cta {
  margin-top: 40px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

// Playhead animation -------------------------------------------------------

.ns-anim-frame {
  grid-area: anim;
  height: 380px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 8px 24px 0;
}
.ns-anim-frame svg {
  width: 100%;
  height: 60%;
  overflow: visible;
}
.ns-anim-frame ::v-deep .ns-wf { fill: $ns-ink-soft; opacity: 0.4; }
.ns-anim-frame ::v-deep .ns-wf-played { fill: $ns-accent; }
.ns-anim-frame .ns-cursor { fill: #fff; }
.ns-anim-frame .ns-cursor    { animation: ns-sweep 12.5s linear infinite; }
.ns-anim-frame .ns-clip-rect { animation: ns-clip 12.5s linear infinite; }
@keyframes ns-sweep {
  0%   { transform: translateX(0); }
  100% { transform: translateX(100%); }
}
@keyframes ns-clip {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(0); }
}
@media (prefers-reduced-motion: reduce) {
  .ns-anim-frame .ns-cursor,
  .ns-anim-frame .ns-clip-rect { animation: none; }
}

// Pull quote ---------------------------------------------------------------

.ns-pullquote {
  max-width: 940px;
  margin: 0 auto;
  padding: 120px 20px 24px;
  text-align: center;
  position: relative;
}
.ns-pullquote::before {
  // unquote() lets Sass emit the CSS hex escape verbatim ("\201C" — 5 ASCII
  // chars) instead of resolving it to U+201C and writing the raw UTF-8 bytes
  // into the compiled CSS. Some downstream step in the Vue-CLI 5 CSS pipeline
  // round-trips those bytes through Latin-1 and produces mojibake ("â€œ");
  // keeping the escape unresolved until the browser parses the CSS at runtime
  // sidesteps the whole encoding hazard.
  content: unquote('"\\201C"');
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(-50%);
  font-family: "Euclid Circular", sans-serif;
  font-size: 260px;
  line-height: 1;
  color: $ns-accent;
  opacity: 0.4;
  pointer-events: none;
}
.ns-pullquote p {
  font-family: "Euclid Circular", sans-serif;
  font-weight: 400;
  font-style: italic;
  font-size: 30px;
  line-height: 1.35;
  color: $ns-paper;
  text-wrap: balance;
  margin: 0;
}

// Tracks -------------------------------------------------------------------

.ns-tracks-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 24px 0;
  border-top: 1px solid $bluegrey6;
  border-bottom: 1px solid $bluegrey6;
}
.ns-tracks-cta {
  margin-top: 40px;
  text-align: center;
}

// Best for -----------------------------------------------------------------

.ns-best {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  border-top: 1px solid $ns-rule-hard;
}
.ns-best-item {
  display: grid;
  grid-template-columns: 60px 1fr;
  gap: 20px;
  padding: 30px 0;
  border-bottom: 1px solid $ns-rule-hard;
  align-items: baseline;
}
.ns-best-idx {
  font-family: "Euclid Triangle", sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  font-weight: 500;
  font-style: normal;
  font-size: 11px;
  color: $ns-accent;
}
.ns-best-label {
  display: block;
  font-family: "GTAmerica", sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-size: 20px;
  color: $ns-paper;
  line-height: 1.15;
}
.ns-best-item:nth-last-child(-n+2) { border-bottom: 0; }
@media (max-width: 720px) {
  .ns-best { grid-template-columns: 1fr; }
  .ns-best-item:nth-last-child(2) { border-bottom: 1px solid $ns-rule-hard; }
}

// Licensing ----------------------------------------------------------------

.ns-licensing {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  padding: 56px;
  background: linear-gradient(180deg, rgba(246, 178, 62, 0.08), rgba(246, 178, 62, 0.04));
  border: 1px solid $ns-yellow;
  border-left-width: 4px;
}
.ns-licensing-headline {
  font-family: "GTAmerica", sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-size: 34px;
  line-height: 1.1;
  color: $ns-paper;
  text-wrap: balance;
  margin: 0;
}
.ns-licensing-headline em {
  color: $ns-yellow;
  font-style: normal;
}
.ns-licensing-body { color: $ns-ink-1; font-size: 15px; line-height: 1.65; }
.ns-licensing-body p + p { margin-top: 12px; }
.ns-marks {
  margin-top: 24px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.ns-marks span {
  padding: 8px 12px;
  border: 1px solid $ns-yellow;
  color: $ns-yellow;
  background: transparent;
  font-family: "Euclid Triangle", sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  font-weight: 500;
  font-size: 10px;
}
@media (max-width: 820px) {
  .ns-licensing { grid-template-columns: 1fr; padding: 32px; gap: 28px; }
  .ns-licensing-headline { font-size: 26px; }
}

// FAQ ----------------------------------------------------------------------

.ns-faq-list { display: flex; flex-direction: column; }
.ns-faq-item {
  padding: 34px 0;
  border-top: 1px solid $ns-rule-hard;
  display: grid;
  grid-template-columns: 42% 1fr;
  gap: 40px;
}
.ns-faq-item:last-child { border-bottom: 1px solid $ns-rule-hard; }
.ns-faq-q {
  font-family: "Euclid Circular", sans-serif;
  font-style: italic;
  font-weight: 500;
  font-size: 22px;
  line-height: 1.3;
  color: $ns-paper;
  text-wrap: balance;
}
.ns-faq-a {
  color: $ns-ink-1;
  font-size: 15px;
  line-height: 1.7;
  max-width: 58ch;
}
@media (max-width: 720px) {
  .ns-faq-item { grid-template-columns: 1fr; gap: 12px; }
  .ns-faq-q { font-size: 18px; }
}

// Related links -----------------------------------------------------------

.ns-related-block + .ns-related-block { margin-top: 44px; }
.ns-related-heading {
  font-family: "Euclid Triangle", sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  font-weight: 500;
  font-style: italic;
  font-size: 11px;
  color: $ns-accent;
  margin: 0 0 16px;
}
.ns-related-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  list-style: none;
  margin: 0;
  padding: 0;
}
.ns-related-list li { list-style: none; }
.ns-related-list a {
  display: inline-block;
  padding: 10px 16px;
  border: 1px solid $ns-rule-hard;
  color: $ns-paper;
  font-family: "Euclid Circular", sans-serif;
  font-size: 14px;
  text-decoration: none;
  transition: border-color .2s ease, color .2s ease;
}
.ns-related-list a:hover {
  border-color: $ns-accent;
  color: $ns-accent;
}

// Finale -------------------------------------------------------------------

.ns-finale {
  text-align: center;
  padding: 150px 0 170px;
  border-top: 1px solid $ns-rule-hard;
  background:
    radial-gradient(ellipse at center bottom, rgba($red, 0.14) 0%, transparent 55%),
    $ns-bg-0;
}
.ns-finale h2 {
  font-family: "GTAmerica", sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-size: 82px;
  line-height: 0.98;
  color: $ns-paper;
  text-wrap: balance;
  max-width: 20ch;
  margin: 0 auto;
}
.ns-finale h2 em { color: $ns-accent; font-style: normal; }
.ns-finale-title-link {
  color: inherit;
  text-decoration: none;
  transition: color .2s ease;
}
.ns-finale-title-link:hover em { color: $ns-paper; }
.ns-finale-cta {
  margin-top: 52px;
  display: inline-flex;
  gap: 14px;
}

// Responsive ---------------------------------------------------------------

@media (max-width: 900px) {
  .ns-hero { padding: 56px 0 72px; }
  .ns-section { padding: 72px 0; }
  .ns-hero-grid {
    grid-template-columns: 1fr;
    grid-template-areas: "title" "body" "anim";
    gap: 32px;
  }
  .ns-hero-title { font-size: clamp(48px, 12vw, 84px); }
  .ns-anim-frame { height: 220px; }
  .ns-section-head { grid-template-columns: 1fr; gap: 12px; margin-bottom: 32px; }
  .ns-section-title { font-size: 30px; }
  .ns-finale h2 { font-size: clamp(38px, 8vw, 60px); }
  .ns-pullquote::before { top: -60px; font-size: 160px; }
  .ns-pullquote p { font-size: 22px; }
}
</style>
